import { Check } from 'lucide-react';

interface Tier {
  name: string;
  price: string;
  period?: string;
  desc: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

const tiers: Tier[] = [
  {
    name: 'Starter',
    price: '₹0',
    period: '/forever',
    desc: 'For solo shops and side-businesses just getting started.',
    features: [
      'Up to 100 invoices/month',
      'GST-ready invoicing & e-Way Bill',
      '1 user, 1 branch',
      'Basic POS & inventory',
      'Email support',
    ],
    cta: 'Start free',
  },
  {
    name: 'Growth',
    price: '₹999',
    period: '/branch/month',
    desc: 'For growing retailers, restaurants and distributors.',
    features: [
      'Unlimited invoices',
      'e-Invoice (IRN) + e-Way Bill',
      'Up to 10 users, multi-branch',
      'POS, restaurant, purchase & inventory',
      'Bank reconciliation & TDS',
      'Priority email + chat support',
    ],
    cta: 'Start 14-day trial',
    highlighted: true,
  },
  {
    name: 'Business',
    price: '₹2,499',
    period: '/branch/month',
    desc: 'For multi-branch businesses that need full ERP.',
    features: [
      'Everything in Growth',
      'Unlimited users',
      'Full accounting + payroll + HRM',
      'CRM, AI insights & workflows',
      'Custom roles & approval matrices',
      'Dedicated success manager',
    ],
    cta: 'Talk to sales',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    desc: 'White-label & on-prem deployments for groups.',
    features: [
      'Everything in Business',
      'White-label / multi-tenant resell',
      'On-prem or VPC deployment',
      'SSO, SAML, custom SLA',
      'API + webhook integrations',
    ],
    cta: 'Contact us',
  },
];

export default function Pricing() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" id="pricing">
      <div className="max-w-2xl">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          Simple pricing that scales with you
        </h2>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)] sm:text-base">
          No setup fees. No per-invoice charge. Cancel anytime.
        </p>
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-4">
        {tiers.map((t) => (
          <div
            key={t.name}
            className={`flex flex-col rounded-lg border p-6 ${
              t.highlighted
                ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                : 'border-[var(--color-border)] bg-[var(--color-bg-card)]'
            }`}
          >
            <div className="flex items-baseline justify-between">
              <div className="text-sm uppercase tracking-wide text-[var(--color-text-secondary)]">
                {t.name}
              </div>
              {t.highlighted && (
                <span className="rounded-full bg-[var(--color-primary)] px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                  Popular
                </span>
              )}
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-3xl font-semibold">{t.price}</span>
              {t.period && (
                <span className="text-xs text-[var(--color-text-muted)]">{t.period}</span>
              )}
            </div>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{t.desc}</p>

            <ul className="mt-4 flex-1 space-y-2 text-sm">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check
                    size={14}
                    className="mt-1 shrink-0 text-[var(--color-primary)]"
                  />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <a
              href="/signup"
              className={`mt-6 inline-flex h-10 items-center justify-center rounded-md text-sm font-medium ${
                t.highlighted
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'border border-[var(--color-border)] text-[var(--color-text-primary)]'
              }`}
            >
              {t.cta}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
